import {objectKeyCount, objectKeys} from '@/utils/objectUtils';

export function validateObservation(observation) {
    if (!observation.person) {
        throw new Error('Observation is missing person');
    }

    if (!observation.interactionPoint) {
        throw new Error('Observation is missing interaction point');
    }

    if (observation.hasDependency && !observation.dependencyObservationId) {
        throw new Error('Observation has dependency but no dependency observation');
    }

    if (observation.type === 'visualInspection') {
        if (!observation.contentInspection) {
            throw new Error('Observation is missing inspection content');
        }
    } else if (observation.type === 'question') {
        if (!observation.contentQuestion) {
            throw new Error('Observation is missing question content');
        }

        if (!observation.contentAnswer) {
            throw new Error('Observation is missing answer content');
        }
    } else {
        throw new Error('Unknown observation type "' + observation.type + '"');
    }
}

export function validateObservationGroup(observationGroup, scanChildren = false) {
    if (!observationGroup.observations) {
        throw new Error('Observation group has no observations');
    }

    if (objectKeyCount(observationGroup.observations) < 1) {
        throw new Error('Observation group has no observations');
    }

    if (scanChildren) {
        for (let id in observationGroup.observations) {
            let observation = observationGroup.observations[id];

            if (!observation) {
                throw new Error(`ObservationGroup.Observation with id ${id} was falsy`);
            }

            validateObservation(observation);
        }
    }

    let oneObservationRequired = false;

    for (let id in observationGroup.observations) {
        let observation = observationGroup.observations[id];

        if (observation && observation.required) {
            oneObservationRequired = true;
        }
    }

    if (!oneObservationRequired) {
        throw new Error('No observations are required - a minimum of one is necessary for the scenario to function')
    }


    /*this.error = null;


    if (!this.observationIds.length) {
        this.error = 'At least one observation is required.';

        return;
    }

    let hasAtLeastOneRequired = false;

    for (let observationId in this.observations) {
        let observation = this.observations[observationId];

        if (observation.required) {
            hasAtLeastOneRequired = true;
        }
    }

    if (!hasAtLeastOneRequired) {
        //this.error = 'At least one observation needs to be set as required.';
    }*/


    let hasCircularDependencies = doesObservationsHaveCircularDependencies(observationGroup.observations);

    if (hasCircularDependencies) {
        throw new Error('Observation group has circular dependencies which isn\'t allowed')
    }
}

export function validateIndicator(indicator, scanChildren = false) {
    if (!indicator.observationGroups) {
        throw new Error('Indicator has no observation groups');
    }

    let observationGroupIds = objectKeys(indicator.observationGroups);

    if (observationGroupIds.length < 1) {
        throw new Error('Indicator has no observation groups');
    }

    if (scanChildren) {
        for (let id in indicator.observationGroups) {
            let observationGroup = indicator.observationGroups[id];

            if (!observationGroup) {
                throw new Error(`Indicator with id ${id} has no observation group`);
            }

            validateObservationGroup(observationGroup, scanChildren);
        }
    }
}

export function doesObservationsHaveCircularDependencies(observations) {
    //console.log('doesObservationsHaveCircularDependencies', observations);

    for (let observationId in observations) {
        let observation = observations[observationId];

        if (observation.hasDependency) {
            let dependencyObservation = observation;

            //console.log('dependencyObservation', dependencyObservation, observation);

            if (dependencyObservation.dependencyObservationId) {
                let dependencyChainIds = [];

                while (dependencyObservation.dependencyObservationId) {
                    if (dependencyChainIds.indexOf(dependencyObservation.dependencyObservationId) >= 0) {
                        // The dependency is circular !!!
                        //console.log(dependencyChainIds, 'dependencyChainIds already have dependency id', dependencyObservation.dependencyObservationId);

                        return true;
                    }

                    dependencyChainIds.push(dependencyObservation.dependencyObservationId);

                    if (!observations.hasOwnProperty(dependencyObservation.dependencyObservationId)) {
                        break;
                        //throw new Error(`Could not find observation by id ${dependencyObservation.dependencyObservationId}`);
                    }

                    dependencyObservation = observations[dependencyObservation.dependencyObservationId];

                    //console.log('dependencyObservation.change', dependencyObservation);
                }
            }
        }
    }

    return false;
}

export function validateScenarioIndicator(indicator, scenarioIndicators, observationDependencies = null) {
    if (!observationDependencies) {
        observationDependencies = getObservationDependenciesFromIndicators(scenarioIndicators);
        //console.log(observationDependencies);
    }

    if (!indicator.indicatorId) {
        throw new Error('Indicator is missing indicator');
    }

    if (!indicator.observationGroupId) {
        throw new Error('Indicator is missing observation group');
    }

    if (indicator.dependencies) {
        for (let dependencyId in indicator.dependencies) {
            let dependency = indicator.dependencies[dependencyId];

            validateObservationDependency(dependency, scenarioIndicators, dependencyId);
        }
    }

    if (checkIndicatorForCircularObservationDependencies(indicator, observationDependencies)) {
        throw new Error('Indicator has circular dependencies');
    }
}

export function validateScenarioPerson(person, scenario) {
    if (!person.age) {
        throw new Error('Person is missing age');
    }

    if (typeof(person.ethnicity) === 'undefined' || person.ethnicity === '' || person.ethnicity === null || person.ethnicity === false) {
        throw new Error('Person is missing ethnicity');
    }

    if (!person.gender) {
        throw new Error('Person is missing gender');
    }

    if (!person.type) {
        throw new Error('Person is missing type');
    }

    if (typeof(person.hasMobile) === 'undefined' || person.hasMobile === '' || person.hasMobile === null) {
        throw new Error('Person is missing whether or not they have a mobile');
    }
}

export function validateScenario(scenario, session = null) {
    if (session) {
        if (session.difficultyLevel === 'L2' || session.difficultyLevel === 'L3') {
            // If it's L2 or L3 then we also require a sub crime ASSUMING we have said we have a crime
            if (scenario.crime && scenario.crime === 'trafficking' && !scenario.subCrime) {
                throw new Error('An L2/L3 scenario that has a crime also requires a sub crime');
            }
        }
    }

    if (!scenario.people) {
        throw new Error('Scenario has no people');
    }

    if (!scenario.indicators) {
        throw new Error('Scenario has no indicators');
    }

    for (let person of scenario.people) {
        validateScenarioPerson(person, scenario);
    }

    if (scenario.indicators) {
        let observationDependencies = getObservationDependenciesFromIndicators(scenario.indicators);

        for (let indicator of scenario.indicators) {
            validateScenarioIndicator(indicator, scenario.indicators, observationDependencies);
        }
    }
}

function checkIndicatorsForCircularObservationDependencies(indicators, observationDependencies = null) {
    if (!observationDependencies) {
        observationDependencies = getObservationDependenciesFromIndicators(indicators);
    }

    for (let indicator of indicators) {
        if (checkIndicatorForCircularObservationDependencies(indicator, observationDependencies)) {
            return true;
        }
    }

    return false;
}

function checkIndicatorForCircularObservationDependencies(indicator, observationDependencies) {
    if (indicator.dependencies) {
        for (let dependencyObservationId in indicator.dependencies) {
            let _dependencyObservationId = dependencyObservationId;
            let _dependencyChainIds = [];

            while (_dependencyObservationId) {
                if (_dependencyChainIds.indexOf(_dependencyObservationId) >= 0) {
                    return true;
                }

                _dependencyChainIds.push(_dependencyObservationId);

                if (!observationDependencies.hasOwnProperty(_dependencyObservationId)) {
                    break;
                }

                _dependencyObservationId = observationDependencies[_dependencyObservationId];
            }
        }
    }

    return false;
}

function getObservationDependenciesFromIndicators(indicators) {
    let observationDependencies = {};

    for (let indicator of indicators) {
        if (indicator.dependencies) {
            for (let dependencyObservationId in indicator.dependencies) {
                let dependencyObservation = indicator.dependencies[dependencyObservationId];

                observationDependencies[dependencyObservationId] = dependencyObservation.observationId;
            }
        }
    }

    return observationDependencies;
}

export function validateObservationDependency(observationDependency, scenarioIndicators, observationId, scenarioObservations) {
    if (scenarioIndicators.indicators) {
        scenarioIndicators = scenarioIndicators.indicators;
    }

    if (!observationDependency.indicatorId) {
        throw new Error('Dependency is missing an indicator id');
    }

    if (!observationDependency.observationGroupId) {
        //throw new Error('Dependency is missing an observation group id');
        throw new Error('An indicator with a dependency is missing an observation (group). Please select an observation (group and observation) or remove the dependency.');
    }

    if (!observationDependency.observationId) {
        throw new Error('Dependency is missing an observation id');
    }

    if (compareInt(observationDependency.observationId, observationId)) {
        throw new Error('The observation cannot be dependent on itself');
    }

    let indicatorFound = null;

    if (scenarioIndicators) {
        for (let indicator of scenarioIndicators) {
            if (compareInt(indicator.indicatorId, observationDependency.indicatorId) && compareInt(indicator.observationGroupId, observationDependency.observationGroupId)) {
                indicatorFound = observationDependency;

                break;
            }
        }
    }

    if (!indicatorFound) {
        throw new Error('The dependency indicator was not found in the scenario');
    }

    if (scenarioObservations && doesObservationsHaveCircularDependencies(scenarioObservations)) {
        throw new Error('Indicator has circular dependencies');
    }
}

function compareInt(int1, int2) {
    return parseInt(int1, 10) === parseInt(int2, 10);
}
